/**
 * Help Content Configuration
 * Centralized help text for all fields and features
 */

export interface HelpContent {
    description?: string;
    examples?: string[];
    tips?: string[];
    scoring?: {
        base?: string;
        preferred?: string;
        bonus?: string;
    };
}

export const helpContent: Record<string, HelpContent> = {
    // Main system help
    main: {
        description: 'The CV Matching system helps you find the best candidates by analysing CVs against your specific requirements. Use importance levels (Required/Preferred/Bonus) to prioritise criteria.',
        scoring: {
            base: 'Meeting ALL required criteria (deal-breakers)',
            preferred: 'Meeting preferred criteria (proportional: 2/3 met = +20%)',
            bonus: 'Meeting bonus criteria (proportional: 1/2 met = +5%)'
        },
        tips: [
            'Required criteria are deal-breakers - missing ANY disqualifies the CV (0% match)',
            'Preferred criteria improve match quality but aren\'t essential',
            'Bonus criteria distinguish top candidates',
            'Match percentage: 60% (base) + 30% (preferred) + 10% (bonus) = up to 100%',
            'Example: 4/4 required, 1/1 preferred, 0/0 bonus = 60 + 30 + 10 = 100%'
        ]
    },

    // Field-specific help
    geographic: {
        description: 'Enter specific countries or broader regions where experience is needed.',
        examples: [
            'Nepal, Kenya, Tanzania (specific countries)',
            'East Africa, South Asia (regions)',
            'Sub-Saharan Africa (broader region)',
            'West Africa, Sahel region'
        ],
        tips: [
            'Be specific if location is critical',
            'Use regions for flexibility',
            'Can mix countries and regions',
            'Consider neighboring countries for regional experience'
        ]
    },

    methods: {
        description: 'Research and evaluation methodologies and approaches.',
        examples: [
            'Impact evaluation',
            'Qualitative research',
            'Mixed methods',
            'Randomized Controlled Trial (RCT)',
            'Survey design',
            'Case studies',
            'Participatory methods'
        ],
        tips: [
            'Use standard research terminology',
            'Include both qualitative and quantitative if needed',
            'Be specific about evaluation types',
            'Consider methodology families (e.g., experimental, quasi-experimental)'
        ]
    },

    frameworks: {
        description: 'Evaluation frameworks, standards, and approaches used in the field.',
        examples: [
            'Theory of Change',
            'OECD-DAC criteria',
            'Results-Based Management (RBM)',
            'Logical Framework (LogFrame)',
            'UNEG standards',
            'Outcome Harvesting',
            'Most Significant Change'
        ],
        tips: [
            'Use full names or widely-recognized abbreviations',
            'Include organisation-specific frameworks if relevant',
            'Consider both planning and evaluation frameworks'
        ]
    },

    technicalTools: {
        description: 'Statistical software, analysis tools, and technical platforms.',
        examples: [
            'STATA (statistical analysis)',
            'R (statistical programming)',
            'NVivo (qualitative analysis)',
            'SPSS',
            'Python (data analysis)',
            'Excel (advanced)',
            'Power BI (visualization)'
        ],
        tips: [
            'List specific tools, not general categories',
            'Include both statistical and qualitative analysis tools',
            'Specify "advanced" for common tools like Excel',
            'Consider data visualization tools'
        ]
    },

    technicalDomain: {
        description: 'Specific technical or domain expertise areas required for the assignment.',
        examples: [
            'Climate change adaptation',
            'Public health systems',
            'Agricultural value chains',
            'Financial inclusion',
            'Renewable energy',
            'Digital transformation',
            'Supply chain management'
        ],
        tips: [
            'Focus on specific technical knowledge areas',
            'Include both broad domains and niche specializations',
            'Consider cross-cutting technical skills',
            'Think about the core technical competencies needed'
        ]
    },

    communication: {
        description: 'Communication, facilitation, and stakeholder engagement skills.',
        examples: [
            'Workshop facilitation',
            'Report writing',
            'Presentation skills',
            'Stakeholder engagement',
            'Community mobilization',
            'Policy dialogue',
            'Media engagement'
        ],
        tips: [
            'Consider both written and verbal communication',
            'Include facilitation and training skills if needed',
            'Think about stakeholder types (government, community, donors)',
            'Consider cultural communication competencies'
        ]
    },

    professionalEthical: {
        description: 'Professional standards, ethical requirements, and compliance expectations.',
        examples: [
            'Research ethics certification',
            'Data protection compliance',
            'Safeguarding training',
            'Conflict of interest management',
            'UNEG ethical guidelines',
            'Do No Harm principles',
            'Informed consent procedures'
        ],
        tips: [
            'Include mandatory certifications if required',
            'Consider data protection and privacy requirements',
            'Think about safeguarding and ethical research standards',
            'Include professional body memberships if relevant'
        ]
    },

    languages: {
        description: 'Languages required for communication, report writing, or fieldwork.',
        examples: [
            'English',
            'French',
            'Spanish',
            'Arabic',
            'Swahili',
            'Portuguese'
        ],
        tips: [
            'List languages in order of importance',
            'Consider local languages for fieldwork',
            'Specify if written/spoken proficiency matters'
        ]
    },

    sectors: {
        description: 'Thematic areas, sectors, or cross-cutting themes.',
        examples: [
            'GESI (Gender Equality and Social Inclusion)',
            'Health',
            'Education',
            'Agriculture',
            'Climate change',
            'Water & Sanitation',
            'Governance'
        ],
        tips: [
            'Use standard sector names',
            'Include cross-cutting themes (GESI, M&E, Climate)',
            'Be specific about sub-sectors if needed (e.g., Primary education)',
            'Consider both technical sectors and thematic areas'
        ]
    },

    targetPopulations: {
        description: 'Specific groups or populations the work focuses on.',
        examples: [
            'Youth',
            'Women',
            'Refugees',
            'Rural communities',
            'Indigenous peoples',
            'People with disabilities',
            'Smallholder farmers'
        ],
        tips: [
            'Be specific about target groups',
            'Consider vulnerable or marginalized populations',
            'Include age groups if relevant (adolescents, elderly)',
            'Think about geographic populations (urban/rural)'
        ]
    },

    clientTypes: {
        description: 'Organizations or client types the candidate should have experience with.',
        examples: [
            'UN agencies (UNDP, UNICEF, WHO)',
            'ICIMOD',
            'World Bank',
            'Bilateral donors (USAID, DFID, GIZ)',
            'International NGOs',
            'Government ministries',
            'Private foundations'
        ],
        tips: [
            'Name specific organisations if critical',
            'Can use categories (UN agencies, NGOs)',
            'Include both funders and implementers',
            'Consider regional organisations (ICIMOD, African Union)'
        ]
    },

    roleLevel: {
        description: 'Position level, seniority, or role type required.',
        examples: [
            'Senior consultant',
            'Team leader',
            'Project manager',
            'Technical advisor',
            'Evaluation specialist',
            'Principal investigator'
        ],
        tips: [
            'Be clear about seniority expectations',
            'Consider management vs. technical roles',
            'Think about team leadership requirements',
            'Include both title and responsibility level'
        ]
    },

    educationLevel: {
        description: 'Minimum educational qualification required.',
        examples: [
            'Bachelor\'s degree',
            'Master\'s degree',
            'PhD/Doctorate',
            'Professional certification'
        ],
        tips: [
            'Set realistic minimum requirements',
            'Consider if advanced degree is truly necessary',
            'Professional certifications can substitute for degrees in some fields'
        ]
    },

    educationFields: {
        description: 'Preferred fields of study or academic disciplines.',
        examples: [
            'Economics',
            'Public Health',
            'Development Studies',
            'Statistics',
            'Social Sciences',
            'International Relations',
            'Environmental Science'
        ],
        tips: [
            'List relevant academic disciplines',
            'Consider related fields (e.g., Economics and Development Economics)',
            'Think about interdisciplinary backgrounds',
            'Include both technical and social science fields'
        ]
    },

    yearsExperience: {
        description: 'Minimum years of professional experience required.',
        examples: [
            '5+ years (mid-level)',
            '10+ years (senior)',
            '15+ years (very senior)',
            '20+ years (expert)'
        ],
        tips: [
            'Consider the role\'s complexity and responsibility',
            'Balance experience with other qualifications',
            'More years isn\'t always better - focus on relevant experience',
            'Junior roles: 2-5 years, Mid: 5-10 years, Senior: 10+ years'
        ]
    },

    // Importance levels help
    importanceLevels: {
        description: 'Set the importance level for each criterion to prioritise what matters most.',
        tips: [
            'Required ⭐: Must have - Missing ANY required criterion = 0% match (disqualified)',
            'Preferred ◐: Nice to have - Missing reduces match percentage proportionally',
            'Bonus +: Extra credit - Adds small boost to distinguish top candidates',
            'Use Required sparingly - only for true deal-breakers',
            'Most criteria should be Preferred for flexibility',
            'Bonus for distinguishing factors (publications, awards, specific clients)'
        ]
    },

    // Free-text keywords help
    freetextKeywords: {
        description: 'Add keywords to boost CVs containing these terms. This affects ranking but does not filter out CVs.',
        examples: [
            'ICT OR IT OR "Information Technology" — matches any term',
            'FCDO AND USAID — matches CVs with both terms',
            'baseline survey, endline evaluation — comma-separated terms',
            '"climate change adaptation" — exact phrase match'
        ],
        tips: [
            'Use OR between terms to match ANY (e.g., ICT OR IT)',
            'Use AND between terms to match ALL (e.g., FCDO AND USAID)',
            'Use quotes for exact phrases (e.g., "food security")',
            'Use commas to add multiple terms quickly',
            'If you mix OR and AND, the last operator determines the logic',
            'Short acronyms in UPPERCASE (IT, HR, AI) match case-sensitively to avoid common words',
            'Keywords boost ranking — they don\'t filter out CVs without them'
        ]
    }
};
